import { useEffect, useMemo, useState } from "react";
import { Modal, Platform, Pressable, ScrollView, Text, View, type ViewStyle } from "react-native";
import Animated, {
  Easing,
  cancelAnimation,
  useAnimatedStyle,
  useSharedValue,
  withDelay,
  withRepeat,
  withSequence,
  withTiming,
} from "react-native-reanimated";

import { COLORS, RADII, STROKES } from "../../lib/brand";
import { type PaneKey } from "../../lib/brand-routes";
import { PANE_THEMES } from "./paneThemes";
import { AdminButton, AdminModal } from "../admin/ui";
import { SectionHeader } from "../aspects/SectionHeader";
import { AdminSearchPalette } from "../admin/AdminGlobalSearchOverlay";
import type { AdminSearchRow } from "../../lib/adminSearch";
import { ViewAsBannerView } from "../admin/ViewAsBanner";

/* ─────────────────────── Loading screen ─────────────────────── */

export function LoadingScreenDemo({
  visible,
  pane,
  onClose,
}: {
  visible: boolean;
  pane: PaneKey;
  onClose: () => void;
}) {
  const isLight = pane === "Student" || pane === "General";
  const backdropBg = isLight ? COLORS.OFFW.hex : COLORS.BLACK.hex;
  const textColor = isLight ? COLORS.FOREST.hex : COLORS.LINEN.hex;
  const textDim = isLight ? "rgba(28,43,26,0.6)" : "rgba(247,244,238,0.6)";
  const panelBg = isLight ? COLORS.WHITE.hex : COLORS.OFF_BLACK.hex;
  const panelBorder = isLight ? "rgba(28,43,26,0.10)" : "rgba(255,255,255,0.07)";

  // Animation values
  const rotate = useSharedValue(0);
  const dotScale = useSharedValue(1);

  useEffect(() => {
    if (!visible) {
      cancelAnimation(rotate);
      cancelAnimation(dotScale);
      rotate.value = 0;
      dotScale.value = 1;
      return;
    }
    rotate.value = withRepeat(
      withTiming(360, { duration: 2400, easing: Easing.inOut(Easing.cubic) }),
      -1,
      false,
    );
    dotScale.value = withRepeat(
      withSequence(
        withTiming(1.3, { duration: 800, easing: Easing.out(Easing.cubic) }),
        withTiming(1, { duration: 800, easing: Easing.in(Easing.cubic) }),
        withDelay(200, withTiming(1, { duration: 0 })),
      ),
      -1,
      false,
    );
  }, [visible, rotate, dotScale]);

  const ringStyle = useAnimatedStyle(() => ({
    transform: [{ rotate: `${rotate.value}deg` }],
  }));
  const dotStyle = useAnimatedStyle(() => ({
    transform: [{ scale: dotScale.value }],
  }));

  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={onClose}>
      <Pressable
        onPress={onClose}
        style={{
          flex: 1,
          backgroundColor: backdropBg,
          alignItems: "center",
          justifyContent: "center",
          padding: 24,
          gap: 36,
        }}
      >
        {/* Animated sun emblem */}
        <View style={{ width: 120, height: 120, alignItems: "center", justifyContent: "center" }}>
          <Animated.View
            style={[
              {
                position: "absolute",
                width: 120,
                height: 120,
                borderRadius: 999,
                borderWidth: 1,
                borderColor: COLORS.SAGE.hex,
              },
              ringStyle,
            ]}
          />
          <View
            style={{
              width: 70,
              height: 70,
              borderRadius: 999,
              borderWidth: 2,
              borderColor: COLORS.AMBER.hex,
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Animated.View
              style={[
                {
                  width: 22,
                  height: 22,
                  borderRadius: 999,
                  backgroundColor: COLORS.AMBER.hex,
                },
                dotStyle,
              ]}
            />
          </View>
        </View>

        {/* Explanation panel */}
        <Pressable
          onPress={(e) => e.stopPropagation()}
          style={{
            maxWidth: 520,
            backgroundColor: panelBg,
            borderRadius: 12,
            borderWidth: 1,
            borderColor: panelBorder,
            padding: 24,
            gap: 12,
            ...(Platform.OS === "web"
              ? ({ boxShadow: "0 12px 36px rgba(19,30,18,0.25)" } as unknown as ViewStyle)
              : null),
          }}
        >
          <Text
            style={{
              fontFamily: "DMSans_700Bold",
              fontSize: 10.5,
              letterSpacing: 2.4,
              textTransform: "uppercase",
              color: COLORS.AMBER.hex,
            }}
          >
            Loading Screen
          </Text>
          <Text
            style={{
              fontFamily: "CormorantGaramond_400Regular",
              fontSize: 26,
              lineHeight: 30,
              color: textColor,
            }}
          >
            <Text style={{ fontFamily: "CormorantGaramond_400Regular_Italic", color: COLORS.AMBER.hex }}>
              Branded
            </Text>{" "}
            waits, never spinners.
          </Text>
          <Text
            style={{
              fontFamily: "DMSans_400Regular",
              fontSize: 14,
              lineHeight: 22,
              color: textDim,
            }}
          >
            Shown during initial portal load, route transitions over 400ms, and any blocking data
            fetch. Backdrop matches the destination surface so the page emerges into context. The
            sun emblem rotates 2.4s per cycle; the inner dot pulses 1.6s. Click anywhere to dismiss.
          </Text>
        </Pressable>
      </Pressable>
    </Modal>
  );
}

/* ─────────────────────── Standard overlay ─────────────────────── */

export function StandardOverlayDemo({
  visible,
  pane,
  onClose,
}: {
  visible: boolean;
  pane: PaneKey;
  onClose: () => void;
}) {
  const isLight = pane === "Student" || pane === "General";
  const panelBg = isLight ? COLORS.LINEN.hex : COLORS.OFF_BLACK.hex;
  const headerBg = isLight ? "#FAF7F0" : "rgba(255,255,255,0.03)";
  const border = isLight ? COLORS.SAND_DARK.hex : "rgba(255,255,255,0.08)";
  const text = isLight ? COLORS.FOREST.hex : COLORS.LINEN.hex;
  const textDim = isLight ? "rgba(28,43,26,0.6)" : "rgba(247,244,238,0.6)";

  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={onClose}>
      <Pressable
        onPress={onClose}
        style={{
          flex: 1,
          backgroundColor: "rgba(14,14,14,0.55)",
          alignItems: "center",
          justifyContent: "center",
          padding: 24,
          ...(Platform.OS === "web"
            ? ({ backdropFilter: "blur(8px)" } as unknown as object)
            : null),
        }}
      >
        <Pressable
          onPress={(e) => e.stopPropagation()}
          style={{
            width: "100%",
            maxWidth: 720,
            maxHeight: "85%",
            backgroundColor: panelBg,
            borderRadius: 12,
            borderWidth: 1,
            borderColor: border,
            overflow: "hidden",
          }}
        >
          {/* Header */}
          <View
            style={{
              flexDirection: "row",
              justifyContent: "space-between",
              alignItems: "center",
              paddingVertical: 14,
              paddingHorizontal: 22,
              borderBottomWidth: 1,
              borderBottomColor: border,
              backgroundColor: headerBg,
            }}
          >
            <View>
              <Text
                style={{
                  fontFamily: "DMSans_600SemiBold",
                  fontSize: 14,
                  color: text,
                  marginBottom: 2,
                }}
              >
                Settings
              </Text>
              <Text style={{ fontFamily: "DMSans_400Regular", fontSize: 11, color: textDim }}>
                Sample of the standard overlay pattern
              </Text>
            </View>
            <Pressable
              onPress={onClose}
              accessibilityRole="button"
              style={({ hovered }) => [
                {
                  width: 30,
                  height: 30,
                  borderRadius: 999,
                  borderWidth: 1,
                  borderColor: isLight ? "rgba(28,43,26,0.18)" : "rgba(255,255,255,0.18)",
                  alignItems: "center",
                  justifyContent: "center",
                },
                Platform.OS === "web" && hovered
                  ? ({
                      backgroundColor: isLight ? "rgba(28,43,26,0.05)" : "rgba(255,255,255,0.06)",
                    } as ViewStyle)
                  : null,
              ]}
            >
              <Text style={{ fontSize: 16, color: textDim, lineHeight: 16 }}>×</Text>
            </Pressable>
          </View>

          <ScrollView contentContainerStyle={{ padding: 22, gap: 22 }} showsVerticalScrollIndicator={false}>
            {/* Explanation */}
            <View style={{ gap: 10 }}>
              <Text
                style={{
                  fontFamily: "DMSans_700Bold",
                  fontSize: 10,
                  letterSpacing: 2.4,
                  textTransform: "uppercase",
                  color: COLORS.AMBER.hex,
                }}
              >
                Purpose
              </Text>
              <Text
                style={{
                  fontFamily: "CormorantGaramond_500Medium",
                  fontSize: 22,
                  lineHeight: 28,
                  color: text,
                }}
              >
                The standard overlay pattern. Used everywhere we surface secondary content without
                navigating away.
              </Text>
              <Text style={{ fontFamily: "DMSans_400Regular", fontSize: 14, lineHeight: 22, color: textDim }}>
                Triggered from topbar icons (Student portal) or sidebar buttons (Client, Admin).
                The backdrop blurs the underlying page so the user knows where they are. Click
                outside or the × to dismiss. Used for: Settings, Activity feed, Profile,
                Announcements / Mail, Search results, MFA enrollment, and any other modal
                workspace.
              </Text>
            </View>

            {/* Sample content */}
            <View
              style={{
                borderTopWidth: 1,
                borderTopColor: border,
                paddingTop: 18,
                gap: 14,
              }}
            >
              <Text
                style={{
                  fontFamily: "DMSans_700Bold",
                  fontSize: 10,
                  letterSpacing: 2.4,
                  textTransform: "uppercase",
                  color: textDim,
                }}
              >
                Sample content
              </Text>
              {[
                {
                  label: "Email notifications",
                  meta: "Daily digest of new competitions",
                  on: true,
                },
                {
                  label: "Profile visibility",
                  meta: "Hiring companies can find you",
                  on: true,
                },
                {
                  label: "Marketing updates",
                  meta: "Occasional product news",
                  on: false,
                },
              ].map((row) => (
                <View
                  key={row.label}
                  style={{
                    flexDirection: "row",
                    justifyContent: "space-between",
                    alignItems: "center",
                    paddingVertical: 12,
                    paddingHorizontal: 14,
                    borderWidth: 1,
                    borderColor: border,
                    borderRadius: 8,
                    backgroundColor: isLight ? COLORS.WHITE.hex : "rgba(255,255,255,0.02)",
                  }}
                >
                  <View>
                    <Text
                      style={{
                        fontFamily: "DMSans_500Medium",
                        fontSize: 13,
                        color: text,
                      }}
                    >
                      {row.label}
                    </Text>
                    <Text style={{ fontFamily: "DMSans_400Regular", fontSize: 11, color: textDim }}>
                      {row.meta}
                    </Text>
                  </View>
                  <View
                    style={{
                      width: 38,
                      height: 22,
                      borderRadius: 999,
                      backgroundColor: row.on
                        ? COLORS.AMBER.hex
                        : isLight
                          ? "rgba(28,43,26,0.18)"
                          : "rgba(255,255,255,0.15)",
                      padding: 2,
                      alignItems: row.on ? "flex-end" : "flex-start",
                    }}
                  >
                    <View
                      style={{
                        width: 18,
                        height: 18,
                        borderRadius: 999,
                        backgroundColor: COLORS.WHITE.hex,
                      }}
                    />
                  </View>
                </View>
              ))}
            </View>
          </ScrollView>
        </Pressable>
      </Pressable>
    </Modal>
  );
}

/* ─────────────────────── Notification overlay ─────────────────────── */

export function NotificationOverlayDemo({
  visible,
  pane,
  onClose,
}: {
  visible: boolean;
  pane: PaneKey;
  onClose: () => void;
}) {
  const isLight = pane === "Student" || pane === "General";
  const panelBg = isLight ? COLORS.WHITE.hex : COLORS.OFF_BLACK.hex;
  const border = isLight ? COLORS.SAND_DARK.hex : "rgba(255,255,255,0.08)";
  const text = isLight ? COLORS.FOREST.hex : COLORS.LINEN.hex;
  const textDim = isLight ? "rgba(28,43,26,0.6)" : "rgba(247,244,238,0.6)";

  // Slide-in animation
  const offset = useSharedValue(60);
  const opacity = useSharedValue(0);

  useEffect(() => {
    if (visible) {
      offset.value = withTiming(0, { duration: 380, easing: Easing.out(Easing.cubic) });
      opacity.value = withTiming(1, { duration: 280 });
    } else {
      offset.value = 60;
      opacity.value = 0;
    }
  }, [visible, offset, opacity]);

  const cardAnim = useAnimatedStyle(() => ({
    opacity: opacity.value,
    transform: [{ translateY: offset.value }],
  }));

  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={onClose}>
      <Pressable
        onPress={onClose}
        style={{
          flex: 1,
          backgroundColor: "rgba(14,14,14,0.35)",
          alignItems: "center",
          justifyContent: "flex-start",
          paddingTop: 80,
          paddingHorizontal: 24,
        }}
      >
        <Animated.View
          style={[
            {
              width: "100%",
              maxWidth: 480,
            },
            cardAnim,
          ]}
        >
          <Pressable onPress={(e) => e.stopPropagation()}>
            {/* The notification card itself */}
            <View
              style={{
                backgroundColor: panelBg,
                borderRadius: 12,
                borderWidth: 1,
                borderColor: border,
                borderLeftWidth: 4,
                borderLeftColor: COLORS.AMBER.hex,
                padding: 18,
                flexDirection: "row",
                gap: 14,
                ...(Platform.OS === "web"
                  ? ({ boxShadow: "0 16px 40px rgba(19,30,18,0.30)" } as unknown as ViewStyle)
                  : null),
              }}
            >
              <SunIconLarge size={36} />
              <View style={{ flex: 1, gap: 6 }}>
                <Text
                  style={{
                    fontFamily: "DMSans_700Bold",
                    fontSize: 9.5,
                    letterSpacing: 2.2,
                    textTransform: "uppercase",
                    color: COLORS.AMBER.hex,
                  }}
                >
                  From Coravu
                </Text>
                <Text
                  style={{
                    fontFamily: "CormorantGaramond_500Medium",
                    fontSize: 19,
                    lineHeight: 24,
                    color: text,
                  }}
                >
                  Your profile is 60% complete.
                </Text>
                <Text
                  style={{
                    fontFamily: "DMSans_400Regular",
                    fontSize: 13,
                    lineHeight: 20,
                    color: textDim,
                  }}
                >
                  Add your résumé to start receiving competition invites from hiring companies.
                </Text>
                <View style={{ flexDirection: "row", gap: 10, marginTop: 6 }}>
                  <View
                    style={{
                      paddingVertical: 7,
                      paddingHorizontal: 14,
                      backgroundColor: COLORS.AMBER.hex,
                      borderRadius: 4,
                    }}
                  >
                    <Text
                      style={{
                        fontFamily: "DMSans_700Bold",
                        fontSize: 10.5,
                        letterSpacing: 1.4,
                        textTransform: "uppercase",
                        color: "#080808",
                      }}
                    >
                      Complete profile
                    </Text>
                  </View>
                  <View
                    style={{
                      paddingVertical: 7,
                      paddingHorizontal: 14,
                      borderWidth: 1,
                      borderColor: border,
                      borderRadius: 4,
                    }}
                  >
                    <Text
                      style={{
                        fontFamily: "DMSans_700Bold",
                        fontSize: 10.5,
                        letterSpacing: 1.4,
                        textTransform: "uppercase",
                        color: textDim,
                      }}
                    >
                      Later
                    </Text>
                  </View>
                </View>
              </View>
              <Pressable
                onPress={onClose}
                style={{
                  width: 22,
                  height: 22,
                  borderRadius: 999,
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Text style={{ color: textDim, fontSize: 14, lineHeight: 14 }}>×</Text>
              </Pressable>
            </View>

            {/* Explanation panel */}
            <View
              style={{
                marginTop: 16,
                backgroundColor: panelBg,
                borderRadius: 12,
                borderWidth: 1,
                borderColor: border,
                padding: 18,
                gap: 10,
              }}
            >
              <Text
                style={{
                  fontFamily: "DMSans_700Bold",
                  fontSize: 10,
                  letterSpacing: 2.4,
                  textTransform: "uppercase",
                  color: COLORS.AMBER.hex,
                }}
              >
                Notification Overlay
              </Text>
              <Text
                style={{
                  fontFamily: "CormorantGaramond_500Medium",
                  fontSize: 18,
                  lineHeight: 24,
                  color: text,
                }}
              >
                Pushed from Coravu. Not toggleable by the user.
              </Text>
              <Text style={{ fontFamily: "DMSans_400Regular", fontSize: 13, lineHeight: 20, color: textDim }}>
                Triggered by platform events — never by the user themselves. Slides in from the top,
                stays until dismissed or actioned. Amber left-edge marker + sun icon make it visually
                distinct from in-app modals.
              </Text>
              <View style={{ gap: 6, marginTop: 4 }}>
                {[
                  "Profile completion below 80%",
                  "Won a competition (with prize details)",
                  "Deliverable due in 24 hours",
                  "New competition matches profile interests",
                  "Team join request approved",
                  "Hiring company viewed your submission",
                ].map((trigger) => (
                  <View key={trigger} style={{ flexDirection: "row", alignItems: "center", gap: 8 }}>
                    <View
                      style={{
                        width: 4,
                        height: 4,
                        borderRadius: 2,
                        backgroundColor: COLORS.AMBER.hex,
                      }}
                    />
                    <Text style={{ fontFamily: "DMSans_400Regular", fontSize: 12, color: textDim }}>
                      {trigger}
                    </Text>
                  </View>
                ))}
              </View>
            </View>
          </Pressable>
        </Animated.View>
      </Pressable>
    </Modal>
  );
}

/* ─────────────────────── Admin case review (Fix 21) ─────────────────────── */

/**
 * Static reference for the admin "View all" full-case review modal — the
 * blurred-backdrop window admins open from case-approvals to review (and edit)
 * a case's scholarship split, schedule, team size, and brief before approving
 * or sending it back. Reuses the real AdminModal chrome; content is sample.
 */
export function AdminCaseReviewDemo({
  visible,
  onClose,
}: {
  visible: boolean;
  pane: PaneKey;
  onClose: () => void;
}) {
  const t = PANE_THEMES.Admin;
  const demoRow = (k: string, v: string) => (
    <View style={{ flexDirection: "row", marginTop: 4, gap: 8 }}>
      <Text style={{ fontFamily: t.bodyMediumFont, fontSize: 11, color: t.textOnSurfaceDim, minWidth: 120 }}>{k}</Text>
      <Text style={{ flex: 1, fontFamily: t.bodyFont, fontSize: 11.5, color: t.textOnSurface }}>{v}</Text>
    </View>
  );
  const card = {
    padding: 14,
    borderRadius: RADII.md,
    borderWidth: STROKES.hairline,
    borderColor: t.border,
    backgroundColor: t.surfaceRaisedBg,
  } as const;

  return (
    <AdminModal
      theme={t}
      visible={visible}
      onClose={onClose}
      blurBackdrop
      maxWidth={760}
      eyebrow="Full case review"
      title="Acme Growth Sprint"
      subtitle="Acme Corp · Jordan Lee · Coravu RM"
      footer={
        <>
          <AdminButton theme={t} label="Open full editor →" variant="ghost" onPress={onClose} />
          <AdminButton theme={t} label="Edit details" variant="outline" onPress={onClose} />
          <AdminButton theme={t} label="Close" onPress={onClose} />
        </>
      }
    >
      <View style={{ gap: 24 }}>
        <View>
          <SectionHeader theme={t} title="Overview" tone="dark" />
          {demoRow("Tier", "250 students")}
          {demoRow("Engagement", "Coravu RM")}
          {demoRow("Status", "submitted")}
        </View>
        <View>
          <SectionHeader theme={t} title="Scholarship & prize split" tone="dark" />
          <View style={card}>
            <Text style={{ fontFamily: t.displayMediumFont, fontSize: 22, color: t.accent }}>$10,000.00</Text>
            <Text style={{ marginTop: 2, fontFamily: t.bodyFont, fontSize: 11.5, color: t.textOnSurfaceDim }}>
              Total scholarship pool across 3 places
            </Text>
            <View style={{ marginTop: 10, gap: 4 }}>
              {[["1st place", "$6,000.00"], ["2nd place", "$3,000.00"], ["3rd place", "$1,000.00"]].map(([k, v]) => (
                <View key={k} style={{ flexDirection: "row", justifyContent: "space-between" }}>
                  <Text style={{ fontFamily: t.bodyMediumFont, fontSize: 12, color: t.textOnSurface }}>{k}</Text>
                  <Text style={{ fontFamily: t.bodyMediumFont, fontSize: 12, color: t.accent }}>{v}</Text>
                </View>
              ))}
            </View>
          </View>
        </View>
        <View>
          <SectionHeader theme={t} title="Schedule & team" tone="dark" />
          {demoRow("Opens", "Mon, Jun 16, 2026")}
          {demoRow("Closes", "Fri, Jul 18, 2026")}
          {demoRow("Team size", "2–4 members")}
        </View>
        <View>
          <SectionHeader theme={t} title="Brief & deliverables" tone="dark" />
          <View style={card}>
            <Text style={{ fontFamily: t.bodyFont, fontSize: 12.5, lineHeight: 18, color: t.textOnSurface }}>
              Build a go-to-market plan for Acme&apos;s new SMB product line, including positioning,
              channel strategy, and a 90-day launch roadmap.
            </Text>
          </View>
        </View>
      </View>
    </AdminModal>
  );
}

function SunIconLarge({ size }: { size: number }) {
  return (
    <View
      style={{
        width: size,
        height: size,
        borderRadius: 999,
        backgroundColor: "rgba(181,135,58,0.12)",
        borderWidth: 1,
        borderColor: "rgba(181,135,58,0.32)",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <View
        style={{
          width: size * 0.55,
          height: size * 0.55,
          borderRadius: 999,
          borderWidth: 1.4,
          borderColor: COLORS.AMBER.hex,
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <View
          style={{
            width: size * 0.18,
            height: size * 0.18,
            borderRadius: 999,
            backgroundColor: COLORS.AMBER.hex,
          }}
        />
      </View>
    </View>
  );
}

/* ─────────────────── Admin global search palette ─────────────────── */

// Static stand-in rows so the palette renders every result group on /brand
// without an admin session or a network round-trip. The live overlay
// (components/admin/AdminGlobalSearchOverlay) feeds the same presentational
// component from the admin_global_search RPC.
const ADMIN_SEARCH_SAMPLE: AdminSearchRow[] = [
  { kind: "student", id: "s1", title: "Maya Hernandez", subtitle: "maya.h@berkeley.edu", status: null },
  { kind: "judge", id: "j1", title: "Daniel Cho", subtitle: "dcho@mckinsey.com", status: null },
  { kind: "user", id: "u1", title: "Priya Nair", subtitle: "priya@acme.com", status: null },
  { kind: "company", id: "c1", title: "Acme Corp", subtitle: "Consumer goods", status: null },
  { kind: "company", id: "c2", title: "Northwind Labs", subtitle: "Biotech", status: null },
  { kind: "competition", id: "k1", title: "Growth Sprint 2026", subtitle: "Acme Corp", status: "under_review" },
  { kind: "competition", id: "k2", title: "Retail Demand Forecasting", subtitle: "Northwind Labs", status: "open" },
  { kind: "inquiry", id: "i1", title: "Jordan Blake", subtitle: "jordan@prospect.io", status: "new" },
  { kind: "invite", id: "v1", title: "newhire@acme.com", subtitle: "client", status: "pending" },
];

export function AdminGlobalSearchDemo({
  visible,
  onClose,
}: {
  visible: boolean;
  pane: PaneKey;
  onClose: () => void;
}) {
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState(0);

  // Same 2-char floor as the live palette; filter the sample set in memory.
  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (q.length < 2) return [];
    return ADMIN_SEARCH_SAMPLE.filter(
      (r) =>
        r.title.toLowerCase().includes(q) || r.subtitle.toLowerCase().includes(q),
    );
  }, [query]);

  return (
    <AdminSearchPalette
      visible={visible}
      onClose={onClose}
      query={query}
      onQueryChange={(q) => {
        setQuery(q);
        setSelected(0);
      }}
      loading={false}
      rows={rows}
      selectedIndex={selected}
      onHoverIndex={setSelected}
      onActivate={() => onClose()}
      placeholder="Try “acme”, “maya”, “growth”…"
    />
  );
}

/* ─────────────────── "View as" banner ─────────────────── */

// The persistent admin "Viewing as / Editing as" bar, shown in both modes so
// reviewers see the read-only default and the edit-on-behalf state. Live
// version: components/admin/ViewAsBanner. Static here — no impersonation.
export function ViewAsBannerDemo({
  visible,
  pane,
  onClose,
}: {
  visible: boolean;
  pane: PaneKey;
  onClose: () => void;
}) {
  const t = PANE_THEMES[pane];
  const noop = () => {};
  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={onClose}>
      <Pressable
        onPress={onClose}
        style={{
          flex: 1,
          backgroundColor: "rgba(14,14,14,0.55)",
          alignItems: "center",
          justifyContent: "center",
          padding: 24,
          ...(Platform.OS === "web" ? ({ backdropFilter: "blur(8px)" } as unknown as object) : null),
        }}
      >
        <Pressable
          onPress={(e) => e.stopPropagation()}
          style={{
            width: "100%",
            maxWidth: 720,
            borderRadius: RADII.lg,
            overflow: "hidden",
            borderWidth: STROKES.hairline,
            borderColor: t.border,
          }}
        >
          <ViewAsBannerView
            theme={t}
            name="Maya Hernandez"
            sub="Berkeley"
            mode="read"
            onToggleMode={noop}
            onExit={noop}
            editingName={false}
            nameDraft=""
            onNameDraftChange={noop}
            onStartEditName={noop}
            onSaveName={noop}
          />
          <ViewAsBannerView
            theme={t}
            name="Maya Hernandez"
            sub="Berkeley"
            mode="edit"
            onToggleMode={noop}
            onExit={noop}
            editingName
            nameDraft="Maya Hernandez"
            onNameDraftChange={noop}
            onStartEditName={noop}
            onSaveName={noop}
          />
        </Pressable>
      </Pressable>
    </Modal>
  );
}
